import React, { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { registerUser, whoami } from '@/api/auth';
import { useParams } from 'react-router';
import styles from './EmployeeOnboard.module.scss';
import MainLayout from '@/layouts/MainLayout/MainLayout';
import Container from '@/components/ui/Container/Container';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import FormikInput from '@/components/Formik/FormikInput';
import FormikSelect from '@/components/Formik/FormikSelect';
import Button from '@/components/ui/Button/Button';
import { onboardEmployeeSchema } from '@/validations/auth.ts';
import Spinner from '@/components/ui/Spinner/Spinner.tsx';
import GlobalError from '@/components/FormikErrors/GlobalError.tsx';
import handleResponseErrors from '@/utils/handleResponseErrors.ts';
import queryKeys from '@/constants/queryKeys.ts';
import { createOrganizationEmployee, getOrganizationEntities, getOrganizationTokenData } from '@/api/organization.ts';
import FormikPhoneInput from '@/components/Formik/FormikPhoneInput.tsx';
import InfoStatus from '@/components/ui/InfoStatus/InfoStatus.tsx';
import FormikDatePicker from '@/components/Formik/FormikDatePicker.tsx';
import classNames from 'classnames';
import moment from 'moment';
import FlexBlock from '@/components/ui/FlexBlock/FlexBlock.tsx';
import { Option } from '@/components/ui/Select/Select.tsx';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';
import routes from '@/constants/routes.ts';
import { routeAfterAuth } from '@/utils/authRedirect.ts';
import OnboardNewUserRequest = App.Data.Request.OnboardNewUserRequest;

type LockableOnboardField = 'email';

interface RegisterUserRequestExtended extends Omit<OnboardNewUserRequest, 'gender' | 'dateOfBirth' | 'legalEntityId'> {
	officeId?: undefined | number;
	departmentId?: undefined | number;
	costCenterId?: undefined | number;
	gender: undefined | string;
	dateOfBirth: undefined | string;
	legalEntityId: undefined | number;
}

interface EmployeeOnboardProps {
	mode?: 'self' | 'admin';
}

const EmployeeOnboard: React.FC<EmployeeOnboardProps> = ({ mode = 'self' }) => {
	const queryClient = useQueryClient();
	const isAdminMode = mode === 'admin';
	const [showInfoSuccess, setShowInfoSuccess] = useState({ show: false, email: '' });
	const { token } = useParams<{ token: string }>();
	const setWhoami = useWhoamiStore(state => state.setWhoami);
	const adminOrganization = useWhoamiStore(state => state.user?.organization);
	const organizationIdOrToken = isAdminMode ? adminOrganization?.id : token;

	const { data: organizationData, isLoading: organizationDataIsLoading } = useQuery({
		queryKey: [queryKeys.getOrganizationTokenData, token],
		queryFn: () => getOrganizationTokenData(token || ''),
		enabled: !isAdminMode && !!token,
	});

	const {
		data: organizationEntitiesData,
		isError: organizationEntitiesIsError,
		isLoading: organizationEntitiesIsLoading,
		isSuccess: organizationEntitiesIsSuccess,
	} = useQuery({
		queryKey: [queryKeys.getEmployeeOnboardOrganizationsEntities, organizationIdOrToken + 'entities'],
		queryFn: () => getOrganizationEntities(organizationIdOrToken || ''),
		enabled: !!organizationIdOrToken,
	});

	const organization = isAdminMode ? adminOrganization : organizationData;
	const organizationName = organization?.name;
	const isAlkOrg = organization?.id === 8707; // ALK-Abelló

	const [initialValues, setInitialValues] = useState<RegisterUserRequestExtended>({
		firstName: '',
		middleName: '',
		lastName: '',
		email: '',
		jobTitle: '',
		employeeNumber: '',
		phone: '',
		gender: undefined,
		dateOfBirth: undefined,
		legalEntityId: undefined,
		officeId: undefined,
		departmentId: undefined,
		costCenterId: undefined,
	});

	useEffect(() => {
		if (organizationData) {
			setInitialValues(prev => ({
				...prev,
				email: prev.email || organizationData.onboarding?.defaults.email || '',
			}));
		}
	}, [organizationData]);

	useEffect(() => {
		if (organizationEntitiesData) {
			const legalEntityId =
				organizationEntitiesData.legalEntities?.length === 1
					? organizationEntitiesData.legalEntities[0].id
					: undefined;
			const officeId =
				organizationEntitiesData.offices?.length === 1 &&
				legalEntityId === organizationEntitiesData.offices[0].parentId
					? organizationEntitiesData.offices[0].id
					: undefined;
			const costCenterId =
				organizationEntitiesData.costCenters?.length === 1
					? organizationEntitiesData.costCenters[0].id
					: undefined;
			const departmentId =
				organizationEntitiesData.departments?.length === 1
					? organizationEntitiesData.departments[0].id
					: undefined;

			setInitialValues(prev => ({
				...prev,
				legalEntityId,
				officeId,
				costCenterId,
				departmentId,
			}));
		}
	}, [organizationEntitiesData, organizationEntitiesIsSuccess]);

	const formik = useFormik<OnboardNewUserRequest>({
		initialValues: initialValues as OnboardNewUserRequest,
		enableReinitialize: true,
		validationSchema: onboardEmployeeSchema,
		onSubmit: (values, formikHelpers) => handleSubmit(values, formikHelpers),
	});

	useEffect(() => {
		formik.setFieldValue('officeId', undefined);
		organizationEntitiesData?.offices?.length === 1 &&
			formik.values.legalEntityId === organizationEntitiesData.offices[0].parentId &&
			formik.setFieldValue('officeId', organizationEntitiesData.offices[0].id);
	}, [formik.values.legalEntityId]);

	const isFieldLocked = (field: LockableOnboardField): boolean =>
		organizationData?.onboarding?.lockedFields?.includes(field) ?? false;

	const legalEntitiesOptions =
		(organizationEntitiesData?.legalEntities?.map(entity => ({
			label: (
				<FlexBlock alignItems="center" columnGap="8px">
					{entity.countryCode && (
						<img
							src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${entity.countryCode.toUpperCase()}.svg`}
							alt={entity.countryCode}
							width="22"
							height="14"
						/>
					)}
					<span>{entity.name}</span>
				</FlexBlock>
			),
			value: entity.id,
		})) as unknown as Option[]) || [];

	const handleSubmit = async (values: OnboardNewUserRequest, formikHelpers: FormikHelpers<OnboardNewUserRequest>) => {
		try {
			const changedValues: OnboardNewUserRequest = {
				...values,
				dateOfBirth: moment(values.dateOfBirth).format('YYYY-MM-DD'),
			};
			if (isAdminMode) {
				if (!adminOrganization) {
					throw new Error('An organization is required to create an employee.');
				}

				await createOrganizationEmployee(adminOrganization.id, changedValues);
				await queryClient.invalidateQueries({ queryKey: [queryKeys.responsibleUsers] });
			} else {
				await registerUser({ ...changedValues, onboardToken: token || '' });

				const whoamiData = await whoami();

				if (whoamiData.user) {
					setWhoami(whoamiData);
					routeAfterAuth(whoamiData, routes.myClimate);
					return;
				}
			}

			setShowInfoSuccess({ show: true, email: changedValues.email });
			formikHelpers.resetForm();
		} catch (error) {
			handleResponseErrors(error, formikHelpers);
		}
	};

	if (organizationEntitiesIsLoading || organizationDataIsLoading) {
		return (
			<MainLayout>
				<Spinner />
			</MainLayout>
		);
	}

	const hasNoLegalEntities =
		isAdminMode && organizationEntitiesIsSuccess && !organizationEntitiesData?.legalEntities?.length;

	if (organizationEntitiesIsError || (isAdminMode && !adminOrganization) || hasNoLegalEntities) {
		return (
			<MainLayout>
				<Container>
					<div className={styles.employeeOnboardWrapper}>
						<h1>{isAdminMode ? 'Unable to load employee creation' : 'Not Found'}</h1>
						{isAdminMode ? (
							<p>
								{hasNoLegalEntities
									? 'Your organization needs a legal entity before you can create employees. Contact Goodwings support to complete the setup.'
									: 'We could not load your organization details. Refresh the page to try again. If the problem continues, contact Goodwings support.'}
							</p>
						) : (
							<p>
								We're sorry, but the page you are looking for could not be found. Please check the
								information provided and try again. If you continue to experience issues, kindly contact
								our support team for assistance. We apologize for any inconvenience caused.
							</p>
						)}
					</div>
				</Container>
			</MainLayout>
		);
	}

	return (
		<MainLayout>
			<Container>
				<div className={styles.employeeOnboardWrapper}>
					{isAdminMode ? (
						<>
							<h1>Create employee profile for {organizationName}</h1>
							<p>Enter the employee's details below. You will remain signed in to your own account.</p>
						</>
					) : (
						<>
							<h3>
								{organizationName
									? `Set up your Goodwings profile for ${organizationName}`
									: 'Create your free profile'}
							</h3>
							<p>
								We’ve partnered with Goodwings, a modern travel management platform that makes it easy
								for businesses travel more responsibly. With Goodwings you can book your full trip -
								flights, hotels, trains, and rental cars - in one place, while actively reducing your
								travel emissions.
							</p>
							<h3>How it works</h3>
							<p>
								Book through Goodwings, and the platform automatically tracks your emissions, gives you
								real-time insights, and guides you toward smarter, lower-carbon travel choices.
							</p>
							<h3>Let's get started</h3>
							<p>
								Fill in your details below, confirm via email, and you’ll be ready to book responsibly
								in no time.
							</p>
						</>
					)}
					<FormikProvider value={formik}>
						<Form
							onSubmit={event => {
								setShowInfoSuccess({ show: false, email: '' });
								formik.handleSubmit(event);
							}}
						>
							<GlobalError />
							<FormikInput
								name="firstName"
								label="First name*"
								desc={
									isAlkOrg
										? `Must be an exact match to ${isAdminMode ? "the employee's" : 'your'} passport`
										: ''
								}
								className={styles.formikInput}
							/>
							<FormikInput
								name="middleName"
								label="Middle name"
								desc={
									isAlkOrg
										? `Must be an exact match to ${isAdminMode ? "the employee's" : 'your'} passport`
										: ''
								}
								className={styles.formikInput}
							/>
							<FormikInput
								name="lastName"
								label="Last name*"
								desc={
									isAlkOrg
										? `Must be an exact match to ${isAdminMode ? "the employee's" : 'your'} passport`
										: ''
								}
								className={styles.formikInput}
							/>
							<FormikInput
								name="email"
								type="email"
								label="Email*"
								placeholder={isAlkOrg ? 'Use the short ALK email address (XXXDK@ALK.NET)' : ''}
								className={styles.formikInput}
								disabled={isFieldLocked('email')}
							/>
							<FormikInput name="jobTitle" type="text" label="Job title" className={styles.formikInput} />
							<FormikInput
								name="employeeNumber"
								type="text"
								label={isAlkOrg ? 'Initial ID' : 'Employee ID'}
								desc={
									isAlkOrg
										? `Consists of ${isAdminMode ? "the employee's" : 'your'} initials and the country code, or the beginning of ${isAdminMode ? 'their' : 'your'} short email address (e.g., AWJDK).`
										: ''
								}
								className={styles.formikInput}
							/>
							<FormikPhoneInput
								name="phone"
								defaultValue="DK"
								label="Phone*"
								className={styles.formikInput}
							/>
							<FormikSelect
								required
								name="gender"
								label="Gender*"
								options={[
									{ label: 'Male', value: 'MALE' },
									{
										label: 'Female',
										value: 'FEMALE',
									},
								]}
								className={styles.formikInput}
							/>
							<FormikDatePicker
								required
								name="dateOfBirth"
								label="Date of birth*"
								monthYearNavigation
								minDate={moment().subtract(120, 'years')}
								maxDate={moment()}
								calendarDefaultDate={moment().subtract(30, 'years')}
								className={classNames(styles.formikInput, styles.datePicker)}
							/>
							{organizationEntitiesData?.legalEntities &&
								organizationEntitiesData.legalEntities.length > 1 && (
									<FormikSelect
										required
										searchable
										name="legalEntityId"
										options={legalEntitiesOptions}
										label="Legal entity"
										placeholder="Choose legal entity"
										className={styles.formikInput}
									/>
								)}
							{organizationEntitiesData?.offices &&
								organizationEntitiesData.offices.length > 1 &&
								organizationEntitiesData.offices.filter(
									item => item.parentId === formik.values.legalEntityId,
								).length > 1 && (
									<FormikSelect
										required
										searchable
										name="officeId"
										options={organizationEntitiesData.offices
											.filter(item => item.parentId === formik.values.legalEntityId)
											.map(item => ({ value: item.id, label: item.name }))}
										label="Office"
										placeholder="Choose office"
										className={styles.formikInput}
									/>
								)}
							{organizationEntitiesData?.departments &&
								organizationEntitiesData.departments.length > 1 && (
									<FormikSelect
										required
										searchable
										name="departmentId"
										options={organizationEntitiesData.departments.map(item => ({
											value: item.id,
											label: item.name,
										}))}
										label="Department"
										placeholder="Choose department"
										className={styles.formikInput}
									/>
								)}
							{organizationEntitiesData?.costCenters &&
								organizationEntitiesData.costCenters.length > 1 && (
									<FormikSelect
										required
										searchable
										name="costCenterId"
										options={organizationEntitiesData.costCenters.map(item => ({
											value: item.id,
											label: item.name,
										}))}
										label="Cost center"
										placeholder="Choose cost center"
										className={styles.formikInput}
									/>
								)}
							<InfoStatus
								status="warning"
								text="NB. Travellers may be rejected at the airport or entry to a country, if name on the ticket does not match the name in the traveller's passport."
							/>
							{showInfoSuccess.show && (
								<InfoStatus
									status="info"
									text={
										isAdminMode
											? `Employee profile created for ${showInfoSuccess.email}. You can now create another employee.`
											: `The account has been successfully created! A verification link has been sent to ${showInfoSuccess.email}. Please check the email and click the link to activate the account.`
									}
								/>
							)}
							<Button
								type="submit"
								color="green"
								size="large"
								disabled={formik.isSubmitting}
								loading={formik.isSubmitting}
								className={styles.submitButton}
							>
								{isAdminMode ? 'Create employee' : 'Join'}
							</Button>
						</Form>
					</FormikProvider>
				</div>
			</Container>
		</MainLayout>
	);
};

export default EmployeeOnboard;
